import { useEffect, useId, useRef, useState } from 'react'
import { Check } from './icons'

function ChevronDown({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  )
}

/**
 * Themed, accessible single-select dropdown (replaces the native <select> so the
 * open menu matches the app theme instead of the OS chrome).
 *
 * Implements the ARIA select-only combobox pattern: focus stays on the trigger,
 * arrow keys move `aria-activedescendant`, Enter/Space select, Escape closes.
 *
 * @param {{value: string, onChange: (v: string) => void,
 *          options: {value: string, label: string}[],
 *          placeholder?: string, ariaLabel?: string}} props
 */
export default function Select({ value, onChange, options, placeholder = 'Select…', ariaLabel }) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const containerRef = useRef(null)
  const listRef = useRef(null)
  const buttonRef = useRef(null)
  const baseId = useId()
  const listId = `${baseId}-list`
  const optionId = (i) => `${baseId}-opt-${i}`

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null

  function openMenu() {
    setActive(selectedIndex >= 0 ? selectedIndex : 0)
    setOpen(true)
  }
  function closeMenu({ focus = true } = {}) {
    setOpen(false)
    if (focus) buttonRef.current?.focus()
  }
  function choose(i) {
    const opt = options[i]
    if (opt) onChange(opt.value)
    closeMenu()
  }

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Keep the active option in view.
  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  function onKeyDown(e) {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault()
        openMenu()
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => Math.min(i + 1, options.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => Math.max(i - 1, 0))
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(options.length - 1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        choose(active)
        break
      case 'Escape':
        e.preventDefault()
        closeMenu()
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-activedescendant={open ? optionId(active) : undefined}
        onClick={() => (open ? closeMenu({ focus: false }) : openMenu())}
        onKeyDown={onKeyDown}
        className="input flex items-center justify-between gap-2 text-left"
      >
        <span className={`truncate ${selected ? 'text-ink' : 'text-slate-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-30 mt-2 max-h-60 w-full overflow-auto rounded-2xl border border-hairline bg-white/95 p-1.5 shadow-glass-lg backdrop-blur-xl animate-pop-in"
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isActive = i === active
            return (
              <li
                key={opt.value + i}
                id={optionId(i)}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActive(i)}
                // Select on mousedown (+ preventDefault) so the choice commits
                // before the focus/click chain that would otherwise reopen the menu.
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  choose(i)
                }}
                className={`flex cursor-pointer select-none items-center justify-between rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
                  isActive ? 'bg-brand-50' : ''
                } ${isSelected ? 'font-semibold text-brand-700' : 'text-subink'}`}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && <Check className="h-4 w-4 shrink-0 text-brand-600" />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
